import { useWeb3React } from '@web3-react/core';
import { BSC_CHAINID } from 'config';
import {
	FETCH_RECENT_LISTED_NFT_INTERVAL,
	TOTAL_MOST_FAVORITE_NFT_ITEM,
	TOTAL_RECENT_LISTED_NFT_ITEM,
} from 'config/constants/nft';
import { useRequest } from 'hooks/useRequest';
import useSignMessage from 'hooks/useSignMessage';
import { NFTItem, PrepareNFTLoveItem } from 'models/NFT/nftItem.model';
import { State } from 'models/state.model';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NFTSortByEnum } from 'shared/enums/NFT.enum';
import ProductRequest from 'shared/requests/ProductRequest';
import { useAppDispatch } from 'state';
import { setLikeNFT, setMostFavoriteNFTItem, setRecentListedNFT } from '.';

export const useNFTCollection = () => {
	const { execute } = useRequest();
	const dispatch = useAppDispatch();

	const isFetchedMostFavoritedItems = useSelector(
		(state: State) => !!state.nft.mostFavoritedItems.length
	);

	useEffect(() => {
		const fetch = async () => {
			const response = await execute(
				ProductRequest.getProducts(TOTAL_RECENT_LISTED_NFT_ITEM, 0, {
					orderBy: NFTSortByEnum.RecentlyListed,
				})
			);

			dispatch(setRecentListedNFT(response?.data?.items));
		};
		fetch();

		const interval = setInterval(fetch, FETCH_RECENT_LISTED_NFT_INTERVAL);
		return () => {
			clearInterval(interval);
		};
	}, []);

	useEffect(() => {
		if (isFetchedMostFavoritedItems) {
			return;
		}

		const fetch = async () => {
			const response = await execute(
				ProductRequest.getProducts(TOTAL_MOST_FAVORITE_NFT_ITEM, 0, {
					orderBy: NFTSortByEnum.MostFavorited,
				})
			);

			dispatch(setMostFavoriteNFTItem(response?.data?.items));
		};

		fetch();
	}, []);
};

export const useGetLikedNFT = () => {
	const { execute } = useRequest();
	const dispatch = useAppDispatch();
	const { account } = useWeb3React();

	useEffect(() => {
		if (!account) {
			dispatch(setLikeNFT([]));
			return;
		}

		const fetch = async () => {
			const response = await execute(ProductRequest.getLikedProductIds(account));
			const _productIds = response?.data?.productIds || [];
			const _likedNfts = _productIds?.map((item) => {
				return {
					id: item,
					isLike: true,
					totalLike: null,
				};
			});
			dispatch(setLikeNFT(_likedNfts));
		};

		fetch();
	}, [account]);
};

export const useLikeNFT = (item: NFTItem) => {
	const { account } = useWeb3React();
	const { likedNftIds } = useSelector((state: State) => state.nft);

	const { library } = useWeb3React();
	const { signData } = useSignMessage();
	const dispatch = useAppDispatch();
	const totalLikeById = likedNftIds.find((itemLike) => itemLike.id === item.id)?.totalLike;

	const toggleLikeNFT = (toggleLike: boolean) => {
		const messageSignature = toggleLike
			? `[${BSC_CHAINID}] I like the Hunny #${item.id}!`
			: `[${BSC_CHAINID}] I unlike the Hunny #${item.id}!`;

		const handleAfterSigned = async (sign: string) => {
			if (sign) {
				const options: PrepareNFTLoveItem = {
					id: item.id,
					isLike: toggleLike,
					userAdress: account,
					messageSignature: sign,
				};

				const response = await ProductRequest.setLikeProduct(options).call();
				if (response?.code && response.code === 'success') {
					const idxIncrease = likedNftIds.findIndex((itemNFT) => itemNFT?.id === item?.id);
					const _likedNftIds = [...likedNftIds] || [];

					const totalLikeId = _likedNftIds[idxIncrease]?.totalLike;
					const checkTotalLike =
						(totalLikeId || totalLikeById === 0 ? totalLikeId : item?.totalLike) +
						(toggleLike ? 1 : -1);
					const infoLikeNFT = {
						id: item.id,
						isLike: toggleLike,
						totalLike: checkTotalLike > 0 ? checkTotalLike : 0,
					};

					if (idxIncrease > -1) {
						_likedNftIds[idxIncrease] = infoLikeNFT;
					} else {
						_likedNftIds.push(infoLikeNFT);
					}
					dispatch(setLikeNFT([..._likedNftIds]));
				}
			}
		};

		signData(library, messageSignature, handleAfterSigned);
	};
	return {
		toggleLikeNFT,
		totalLike: totalLikeById || totalLikeById === 0 ? totalLikeById : item?.totalLike,
	};
};
